import { Application } from "#/_Bootstrap/Application.ts";
import { registerKicked } from "#/Actions/General/LocationActions.ts";
import {
    enterPresence,
    leavePresence,
    type MemberType,
    overridePresence,
} from "#/Actions/General/PresenceActions.ts";
import {
    insertNotification,
    notificationDeleted,
    type NotificationType,
} from "#/Actions/Notifications/NotificationActions.ts";
import { getLogLevel, LogLevel } from "#/Utilities/Platform/Logging.ts";
import { getOption } from "#/Utilities/Platform/Options/Options.ts";
import { Subchannel } from "#/Workers/Socket/Subchannel.ts";
import { assertNever } from "../../../types/internal/utility";
import AgentManager from "../Bases/AgentManager";
import BaseAgent, { generateWorkerURL } from "../Bases/BaseAgent";
import SyncAgent from "../Sync/SyncAgent";
import { AGENT_EVENTS } from "../Sync/SyncEvents";
import { SOCKET_AGENT_EVENTS, SOCKET_EVENTS, SOCKET_WORKER_EVENTS } from "./SocketEvents";
import { SubchannelConnection } from "./SubchannelConnection.ts";

export interface KickRegisterType {
    register: string;
    user: string;
}

function isKickRegisterData(data: unknown): data is KickRegisterType {
    if(typeof data !== "object") {
        return false;
    }

    if(data === null) {
        return false;
    }

    return "register" in data && typeof data.register === "string" &&
        "user" in data && typeof data.user === "string";
}

interface SocketNotificationType {
    id: string;
    recipient: string;
    data: {
        vendor: string;
        for: "vendor" | "outlet" | "register" | "user";
        image: string | null;
        title: string;
        message: string;
        action: "link" | "download";
        address: string;
    };
    reading?: boolean;
    created_at: string;
    read_at: string | null;
    deleted?: boolean;
}

function convertSocketNotification(data: SocketNotificationType): NotificationType {
    return {
        id       : data.id,
        data     : data.data,
        createdAt: data.created_at,
        readAt   : data.read_at,
    };
}

export function isSocketNotification(data: unknown): data is SocketNotificationType {
    if(typeof data !== "object") {
        return false;
    }

    if(data === null) {
        return false;
    }

    if(
        ("reading" in data && typeof data.reading !== "boolean") ||
        ("deleted" in data && typeof data.deleted !== "boolean")
    ) {
        return false;
    }

    if(!("data" in data)) {
        return false;
    }

    if(typeof data.data !== "object") {
        return false;
    }

    if(data.data === null) {
        return false;
    }

    // Check if not valid "for" value
    if(!(
        "for" in data.data &&
        typeof data.data.for === "string" &&
        [ "vendor", "outlet", "register", "user" ].includes(data.data.for)
    )) {
        return false;
    }

    // Check if not valid "action" value
    if(!(
        "action" in data.data &&
        typeof data.data.action === "string" &&
        [ "link", "download" ].includes(data.data.action)
    )) {
        return false;
    }

    return "id" in data && typeof data.id === "string" &&
        "recipient" in data && typeof data.recipient === "string" &&
        "created_at" in data && typeof data.created_at === "string" &&
        "read_at" in data && (typeof data.read_at === "string" || data.read_at === null) &&
        "vendor" in data.data && typeof data.data.vendor === "string" &&
        "image" in data.data && (typeof data.data.image === "string" || data.data.image === null) &&
        "title" in data.data && typeof data.data.title === "string" &&
        "message" in data.data && typeof data.data.message === "string" &&
        "address" in data.data && typeof data.data.address === "string";
}

export default class SocketAgent extends BaseAgent {
    protected user: false | string;
    protected isAdmin: boolean;
    protected outlet: false | string;
    protected register: false | string;
    protected haveLoadedUser: boolean;
    protected waitingForLoadedUser: boolean;
    protected listeners: Record<string, Set<(data: any) => unknown>>;
    protected subchannel: Record<string, SubchannelConnection>;

    constructor() {
        super(generateWorkerURL("socketworker"), "Shopfront Sockets");

        // Bind handlers
        this.handleMessage = this.handleMessage.bind(this);
        this.handleStoreChange = this.handleStoreChange.bind(this);
        this.updatePresence = this.updatePresence.bind(this);

        // Setup variables
        this.user = false;
        this.isAdmin = false;
        this.outlet = false;
        this.register = false;
        this.haveLoadedUser = false;
        this.waitingForLoadedUser = false;
        this.listeners = {};
        this.subchannel = {};

        // Attempt to get the user
        this.updateUser();

        // Subscribe to user changes
        Application.get("Redux").subscribe(this.handleStoreChange);

        // Start the worker
        this.startWorker();
    }

    public getSubchannel(name: string, id?: string): Subchannel {
        if(!this.subchannel[name]) {
            this.subchannel[name] = new SubchannelConnection(this, name, id);
        }

        return new Subchannel(this.subchannel[name]);
    }

    public removeSubchannel(name: string): void {
        if(!this.subchannel[name]) {
            return;
        }

        this.sendMessage(SOCKET_AGENT_EVENTS.SUBCHANNEL_DISCONNECT, {
            name: this.name,
        });

        delete this.subchannel[name];
    }

    public handleMessage(data: {
        type: SOCKET_WORKER_EVENTS | SOCKET_EVENTS;
        message: any;
    }): void {
        if(!data.type) {
            console.error("Unknown data passed to SocketAgent", data);

            return;
        }

        switch(data.type) {
            case SOCKET_WORKER_EVENTS.REQUEST_USER:
                this.handleUserRequest();
                break;
            case SOCKET_WORKER_EVENTS.RECEIVED_MESSAGE:
                this.handleReceivedMessage(data.message.event, data.message.data);
                break;
            case SOCKET_WORKER_EVENTS.PRESENCE_ENTER:
                this.updatePresence(true, data.message.member);
                break;
            case SOCKET_WORKER_EVENTS.PRESENCE_LEAVE:
                this.updatePresence(false, data.message.member);
                break;
            case SOCKET_WORKER_EVENTS.PRESENCE_OVERRIDE:
                this.overridePresence(data.message.members);
                break;
            case SOCKET_WORKER_EVENTS.SUBCHANNEL_OVERRIDE:
            case SOCKET_WORKER_EVENTS.SUBCHANNEL_ENTER:
            case SOCKET_WORKER_EVENTS.SUBCHANNEL_MESSAGE:
            case SOCKET_WORKER_EVENTS.SUBCHANNEL_LEAVE:
                if(this.subchannel[data.message.channel]) {
                    this.subchannel[data.message.channel].handleMessage(data as {
                        type: SOCKET_WORKER_EVENTS;
                        message: any;
                    });
                }

                break;
            case SOCKET_EVENTS.ROUTINE_SYNC:
                this.handleRoutineSync();
                break;
            default:
                console.error("Unknown data passed to SocketAgent", data);
        }
    }

    protected handleStoreChange(): void {
        const oldUser = this.user;

        this.updateUser();
        this.updateClientId();

        if(!this.user || oldUser === this.user) {
            return;
        }

        this.sendUser();
    }

    protected handleUserRequest(): void {
        if(this.user) {
            this.sendUser();
        }
    }

    protected updateUser(): void {
        const state = Application.get("Redux").getState();

        if(state.user && state.user.id) {
            this.user = state.user.id;
            this.isAdmin = state.user.isAdmin as boolean;
            this.haveLoadedUser = true;

            if(this.waitingForLoadedUser) {
                this.waitingForLoadedUser = false;
                this.updateClientId();
            }
        } else {
            this.user = false;
            this.isAdmin = false;
        }
    }

    protected updateClientId(): void {
        if(!this.haveLoadedUser) {
            this.waitingForLoadedUser = true;

            return;
        }

        let outlet: string | false = false;
        let register: string | false = false;

        const state = Application.get("Redux").getState();

        if(state.location.outlet && state.location.outlet.id) {
            outlet = state.location.outlet.id;
        }

        if(state.location.register && state.location.register.id) {
            register = state.location.register.id;
        }

        let update = false;

        if(outlet !== this.outlet) {
            update = true;
            this.outlet = outlet;
        }

        if(register !== this.register) {
            update = true;
            this.register = register;
        }

        if(update) {
            this.sendMessage(SOCKET_AGENT_EVENTS.UPDATE_ID, {
                outlet    : this.outlet,
                register  : this.register,
                instanceId: getOption("_instance_id"),
                isAdmin   : this.isAdmin,
            });
        }
    }

    protected sendUser(): void {
        this.sendMessage(SOCKET_AGENT_EVENTS.USER, {
            user   : this.user,
            isAdmin: this.isAdmin,
        });
    }

    protected handleReceivedMessage(event: SOCKET_EVENTS, data: unknown): void {
        switch(event) {
            case SOCKET_EVENTS.NOTIFICATION:
                this.handleNotification(data);
                break;
            case SOCKET_EVENTS.DELETE_NOTIFICATION:
                this.handleDeleteNotification(data);
                break;
            case SOCKET_EVENTS.KICK_REGISTER:
                this.handleKickRegister(data);
                break;
            case SOCKET_EVENTS.DISTRIBUTE_ROUTINE_SYNC:
                this.handleRoutineSync();
                break;
            default:
                this.emitEvent(event, data);
                // console.error(`Event type ${event} not implemented`, data);
        }
    }

    protected handleNotification(data: unknown): void {
        if(!isSocketNotification(data)) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.error("Received invalid notification from the socket", data);
            }

            return;
        }

        if(process.env.NODE_ENV !== "production") {
            if(typeof (window as any).testingNotificationReceived === "function") {
                (window as any).testingNotificationReceived(data);
            }
        }

        const state = Application.get("Redux").getState();

        switch(data.data.for) {
            case "vendor":
                // Nothing to check
                break;
            case "outlet":
                if(state.location.outlet?.id !== data.recipient) {
                    return;
                }

                break;
            case "register":
                if(state.location.register?.id !== data.recipient) {
                    return;
                }

                break;
            case "user":
                if(state.user.id !== data.recipient) {
                    return;
                }

                break;
            default:
                assertNever(data.data.for);

                return;
        }

        Application.get("Redux").dispatch(insertNotification(convertSocketNotification(data)));
    }

    protected handleDeleteNotification(id: unknown): void {
        if(typeof id !== "string") {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.error("Received invalid delete notification request from the socket", id);
            }

            return;
        }

        Application.get("Redux").dispatch(notificationDeleted(id, false));
    }

    protected updatePresence(entering: boolean, member: MemberType): void {
        if(entering) {
            Application.get("Redux").dispatch(enterPresence(member));
        } else {
            Application.get("Redux").dispatch(leavePresence(member));
        }
    }

    protected checkRegister(members: Array<MemberType>): boolean {
        if(!this.register) {
            return false;
        }

        for(let i = 0, l = members.length; i < l; i++) {
            const split = members[i].clientId.split("|");
            const register = split[1];
            const id = split[2];

            if(register === this.register && getOption("_instance_id") !== id) {
                return true;
            }
        }

        return false;
    }

    protected overridePresence(members: Array<MemberType>): void {
        const inUse = this.checkRegister(members);

        Application.get("Redux").dispatch(overridePresence(members, inUse));
    }

    protected handleKickRegister(data: unknown): void {
        if(!isKickRegisterData(data)) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.error("Received invalid kick register request from the socket", data);
            }

            return;
        }

        if(this.register && this.register === data.register) {
            Application.get("Redux").dispatch(registerKicked(data.user, data.register));
        }
    }

    protected handleRoutineSync(): void {
        AgentManager.getAgent(SyncAgent).sendMessage(AGENT_EVENTS.SYNC_PERFORM);
    }

    protected emitEvent(event: string, data: any): void {
        if(typeof this.listeners[event] === "undefined") {
            return;
        }

        for(const callback of this.listeners[event]) {
            try {
                callback(data);
            } catch(err) {
                console.error("Error in socket callback", err);
            }
        }
    }

    public addEventListener(event: string, callback: (data: any) => unknown): void {
        if(typeof this.listeners[event] === "undefined") {
            this.listeners[event] = new Set<(data: any) => unknown>();
        }

        this.listeners[event].add(callback);
    }

    public removeEventListener(event: string, callback: (data: any) => unknown): void {
        if(typeof this.listeners[event] === "undefined") {
            return;
        }

        this.listeners[event].delete(callback);
    }
}

export function getSubchannel(name: string, id?: string): Subchannel {
    return AgentManager.getAgent(SocketAgent).getSubchannel(name, id);
}

export function sendSocketMessage(event: string, message: any): void {
    AgentManager.getAgent(SocketAgent).sendMessage(SOCKET_AGENT_EVENTS.BROADCAST, {
        event,
        message,
    });
}
